<script setup lang="ts">
  // PAGADO / FALTA PAGAR box of the POS V2 money pane, with the order's
  // payments folded in: a fully paid order with one payment shows method,
  // age and Reembolsar right in the tile; anything else turns the PAGADO
  // tile into a button that expands the payment rows. Replaces the legacy
  // "Pagos" chevron line + paid summary under POS V2 hosts only.
  import {
    computed,
    getCurrentInstance,
    inject,
    onScopeDispose,
    ref,
    Ref,
    watch,
  } from 'vue';
  import { useElementSize, useNow, useTimeoutFn } from '@vueuse/core';
  import PaymentsService from '@/api/services/payments.service';
  import { OrderPaymentStatus } from '@/enums/OrderEnums';
  import { PaymentEntityType } from '@/enums/PaymentEntityType';
  import { PermissionType } from '@/enums/PermissionType';
  import { IOrder } from '@/interfaces/IOrder';
  import { IPayment } from '@/interfaces/IPayment';
  import {
    getPaymentsBoxState,
    paymentsBoxRowLabelKey,
    canRefundPaymentsBoxRow,
    IPaymentsBoxRow,
    PAYMENTS_BOX_RECONCILE_DELAY_MS,
  } from '@/domain/pos/order-payments-box';
  import { PAYMENTS_BOX_WIDE_MIN } from '@/domain/pos/pos-compaction';
  import { formatRelativeAge } from '@/domain/pos/relative-age';
  import { useAuth } from '@/composition/use-auth';
  import { usePaymentRefund } from '@/composition/use-payment-refund';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { displayPrice } from '@/utils';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiSkeleton from '@/components/pos-v2/ui/PosUiSkeleton.vue';
  import V2PaymentsBoxRow from './V2PaymentsBoxRow.vue';

  interface IPaymentsBoxRowView extends IPaymentsBoxRow {
    label: string;
    ago: string;
    amountText: string;
  }

  const order = inject<Ref<IOrder>>('order')!;
  // POS V2 preview modals re-host this tree read-only: no refund action.
  const posV2ViewMode = inject<'interactive' | 'preview'>(
    'posV2ViewMode',
    'interactive'
  );
  const isPreview = posV2ViewMode === 'preview';

  const auth = useAuth();
  const restaurantStore = useRestaurantStore();
  const { refundPayment } = usePaymentRefund(order);
  const now = useNow({ interval: 30_000 });

  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  // Width-only measurement: the on-screen keyboard changes height, never
  // width, so this tier can't flip under a focused input.
  const rootRef = ref<HTMLElement | null>(null);
  const { width: boxWidth } = useElementSize(rootRef);
  const wide = computed(() => boxWidth.value >= PAYMENTS_BOX_WIDE_MIN);

  const payments = ref<IPayment[] | null>(null);
  const loadError = ref(false);
  // A request for the current totals is in flight; `payments` are the
  // previous rows meanwhile, so no money action may target them.
  const fetching = ref(false);
  const expanded = ref(false);

  const state = computed(() =>
    getPaymentsBoxState({
      order: order.value,
      payments: payments.value,
      loadError: loadError.value,
      refetching: fetching.value,
    })
  );

  // Requests may outlive their usefulness (order switched in place by the
  // table preview, component torn down): only the latest one may land, and
  // nothing starts after teardown.
  let loadSeq = 0;
  let loadedOrderId: number | null = null;
  let disposed = false;
  onScopeDispose(() => {
    disposed = true;
    loadSeq++;
  });

  // Stops itself on scope dispose.
  const { start: scheduleReconcile, stop: cancelReconcile } = useTimeoutFn(
    () => loadPayments({ reconcile: false }),
    PAYMENTS_BOX_RECONCILE_DELAY_MS,
    { immediate: false }
  );

  async function loadPayments(
    options: { reconcile: boolean } = { reconcile: true }
  ): Promise<void> {
    if (disposed) return;
    cancelReconcile();
    const seq = ++loadSeq;
    loadError.value = false;

    const o = order.value;
    // Fully refunded orders sit at totalPaid 0 with status reimbursed and
    // must still list their (struck) rows.
    const mayHavePayments =
      (o.totalPaid ?? 0) > 0 ||
      o.paymentStatus === OrderPaymentStatus.Reimbursed;
    if (!mayHavePayments) {
      payments.value = [];
      fetching.value = false;
      return;
    }

    fetching.value = true;
    try {
      const page = await PaymentsService.v2FindByReference(
        PaymentEntityType.Order,
        o.id,
        {
          itemsPerPage: 1000,
          mustSort: true,
          sortBy: ['createdAt', 'id'],
          sortDesc: [true, true],
        }
      );
      if (seq !== loadSeq) return;
      payments.value = page.items;
      fetching.value = false;
      // Rows that disagree with the totals were most likely read before the
      // server committed them (see PAYMENTS_BOX_RECONCILE_DELAY_MS). One
      // re-read only: a lasting mismatch (legacy rows without allocation
      // data) is real and must not loop.
      if (options.reconcile && state.value.activeTotal !== state.value.paid) {
        scheduleReconcile();
      }
    } catch (e) {
      if (seq !== loadSeq) return;
      // Runs on every order open and offline is a supported mode: no toast,
      // the tile offers an explicit retry instead.
      console.error('Error loading order payments', e);
      loadError.value = true;
      fetching.value = false;
    }
  }

  // Multi-source form: a swapped `order.value` instance with the same
  // figures must not refetch. Previous rows stay while refetching (no
  // skeleton flicker); an order switch resets so another order's rows never
  // show under the new totals.
  watch(
    [
      () => order.value.id,
      () => order.value.totalPaid,
      () => order.value.paymentStatus,
    ],
    ([id]) => {
      if (id !== loadedOrderId) {
        loadedOrderId = id;
        payments.value = null;
        expanded.value = false;
      }
      loadPayments();
    },
    { immediate: true }
  );

  const isPaid = computed(
    () => order.value.paymentStatus === OrderPaymentStatus.Paid
  );

  // Server hard gate: refunds require CajaManage and reject orders owned by
  // a POS device; offering the action there would only end in an error.
  const canRefund = computed(
    () =>
      !isPreview &&
      order.value.posDeviceId == null &&
      auth.hasPermission(PermissionType.CajaManage)
  );

  // Parity with the sibling lines of the costs block (plain displayPrice).
  function formatMoney(value: number): string {
    return `${restaurantStore.currency.symbol}${displayPrice(value)}`;
  }

  const rowsView = computed<IPaymentsBoxRowView[]>(() =>
    state.value.rows.map((row) => ({
      ...row,
      label: i18nT(paymentsBoxRowLabelKey(row)),
      ago: formatRelativeAge(row.createdAt, now.value, i18nT),
      amountText: formatMoney(row.amount),
    }))
  );
  const singleRow = computed<IPaymentsBoxRowView | null>(
    () => rowsView.value[0] ?? null
  );

  const listSubtitle = computed(() => {
    const { rows, activeCount } = state.value;
    const count = String(
      proxy.$tc('posV2.paymentsCount', rows.length, { count: rows.length })
    );
    const methods =
      activeCount > 0
        ? [
            ...new Set(
              rows
                .filter((row) => !row.reimbursed)
                .map((row) => i18nT(paymentsBoxRowLabelKey(row)))
            ),
          ].join(' · ')
        : i18nT('posV2.payReimbursed');
    return `${count} · ${methods}`;
  });

  // Covers the whole flow (confirm, PIN, request, reload) so no second tap
  // can stack a dialog or target a row that is about to be struck.
  const refunding = ref(false);
  const refundDisabled = computed(() => refunding.value || fetching.value);

  async function refund(row: IPaymentsBoxRow) {
    if (refunding.value || !canRefund.value || !canRefundPaymentsBoxRow(row))
      return;
    const payment = payments.value?.find((p) => p.id === row.id);
    if (!payment) return;

    refunding.value = true;
    try {
      const done = await refundPayment({
        payment,
        amount: row.amount,
        shared: row.shared,
      });
      if (disposed || !done) return;

      // The struck row must be visible after the action. Explicit reload so
      // it shows even if the watch-triggered reload raced with the refund.
      expanded.value = true;
      await loadPayments();
    } finally {
      if (!disposed) refunding.value = false;
    }
  }
</script>

<template>
  <div ref="rootRef" class="v2-payments-box" :class="{ 'is-paid': isPaid }">
    <div v-if="state.mode === 'single' && singleRow" class="box-single">
      <div class="box-tile">
        <span class="tile-label">{{ $t('posV2.paidBadge') }}</span>
        <span class="tile-amount is-paid">{{ formatMoney(state.paid) }}</span>
        <span class="tile-sub">
          <PosUiIcon :name="singleRow.icon" class="sub-icon" />
          <span class="sub-text">{{ singleRow.label }}</span>
          <span class="sub-dot">·</span>
          <span class="sub-text">{{ singleRow.ago }}</span>
        </span>
      </div>
      <PosUiButton
        v-if="canRefund && canRefundPaymentsBoxRow(singleRow)"
        color="error"
        variant="soft"
        size="md"
        icon="cash-refund"
        :disabled="refundDisabled"
        class="single-refund"
        @click="refund(singleRow)"
      >
        {{ $t('posV2.paymentsRefund') }}
      </PosUiButton>
    </div>

    <div v-else class="box-tiles">
      <!-- Explicit 'true'/'false': Vue 2 drops attributes bound to false. -->
      <button
        v-if="state.mode === 'list'"
        type="button"
        class="box-tile is-button"
        :aria-expanded="expanded ? 'true' : 'false'"
        @click="expanded = !expanded"
      >
        <span class="tile-label">{{ $t('posV2.paidBadge') }}</span>
        <span class="tile-amount-row">
          <span class="tile-amount is-paid">{{ formatMoney(state.paid) }}</span>
          <PosUiIcon
            name="chevron-down"
            class="tile-chevron"
            :class="{ 'is-open': expanded }"
          />
        </span>
        <span class="tile-sub">
          <span class="sub-text">{{ listSubtitle }}</span>
        </span>
      </button>
      <div v-else class="box-tile">
        <span class="tile-label">{{ $t('posV2.paidBadge') }}</span>
        <span class="tile-amount is-paid">{{ formatMoney(state.paid) }}</span>
        <span v-if="state.mode === 'loading'" class="tile-sub">
          <PosUiSkeleton class="tile-sub-skeleton" />
        </span>
        <span v-else-if="state.mode === 'error'" class="tile-sub">
          <button type="button" class="tile-retry" @click="loadPayments()">
            {{ $t('posV2.retry') }}
          </button>
        </span>
      </div>

      <div class="box-tile tile-to-pay">
        <span class="tile-label">{{ $t('posV2.paymentsToPay') }}</span>
        <span class="tile-amount is-due">{{ formatMoney(state.toPay) }}</span>
      </div>
    </div>

    <div v-if="state.mode === 'list' && expanded" class="box-rows">
      <V2PaymentsBoxRow
        v-for="row in rowsView"
        :key="row.id"
        :icon="row.icon"
        :label="row.label"
        :ago="row.ago"
        :amount-text="row.amountText"
        :reimbursed="row.reimbursed"
        :can-refund="canRefund && canRefundPaymentsBoxRow(row)"
        :disabled="refundDisabled"
        :wide="wide"
        @refund="refund(row)"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-payments-box {
    // No outer margin here: the host's `tw-space-y-1` would override it;
    // ModalPosViewOrderPaymentStatus sets the margin from its own scope.
    @apply tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-bg-neutral-50 tw-p-3;

    // Paid: the costs block's PAGADO watermark reads through.
    &.is-paid {
      @apply tw-border-emerald-200/70 tw-bg-transparent;

      .box-rows {
        @apply tw-border-emerald-200/50;
      }
    }

    .box-tiles {
      @apply tw-flex tw-gap-4;
    }

    .box-single {
      @apply tw-flex tw-items-center tw-gap-3;

      .single-refund {
        @apply tw-flex-none;
      }
    }

    .box-tile {
      @apply tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-0.5;

      // Content width only: the PAGADO subtitle gets every spare pixel
      // before it truncates (on phones the box is ~310px wide).
      &.tile-to-pay {
        @apply tw-flex-none tw-items-end tw-text-right;
      }

      // Native reset: the whole PAGADO tile is the expand control.
      &.is-button {
        @apply tw-w-full tw-cursor-pointer tw-appearance-none tw-rounded-md tw-border-0 tw-bg-transparent tw-p-0 tw-text-left;

        &:focus-visible {
          @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
        }
      }
    }

    .tile-label {
      @apply tw-text-xs tw-font-bold tw-uppercase tw-tracking-wider tw-text-neutral-500;
    }

    .tile-amount-row {
      @apply tw-flex tw-items-center tw-gap-1;

      .tile-chevron {
        @apply tw-text-lg tw-text-neutral-500 tw-transition-transform tw-duration-150;

        &.is-open {
          @apply tw-rotate-180;
        }
      }
    }

    .tile-amount {
      @apply tw-text-lg tw-font-bold tw-tabular-nums tw-text-neutral-900;

      &.is-paid {
        @apply tw-text-emerald-600;
      }

      &.is-due {
        @apply tw-text-xl tw-text-amber-600;
      }
    }

    // One 16px line in every mode (loading / list / single) so the box
    // never jumps when the request lands.
    .tile-sub {
      @apply tw-flex tw-h-4 tw-min-w-0 tw-items-center tw-gap-1 tw-text-xs tw-leading-4 tw-text-neutral-500;

      .sub-icon {
        @apply tw-flex-none tw-text-sm;
      }

      .sub-text {
        @apply tw-truncate;
      }

      .sub-dot {
        @apply tw-flex-none;
      }

      .tile-sub-skeleton {
        @apply tw-h-3 tw-w-28;
      }

      // 16px text line, 44px hit area: the padding is cancelled by the
      // negative margins so the tile keeps its height.
      .tile-retry {
        @apply -tw-mx-2 -tw-my-3.5 tw-cursor-pointer tw-appearance-none tw-border-0 tw-bg-transparent tw-px-2 tw-py-3.5 tw-text-xs tw-font-semibold tw-leading-4 tw-text-neutral-600 hover:tw-underline;

        &:focus-visible {
          @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
        }
      }
    }

    .box-rows {
      @apply tw-mt-3 tw-border-t tw-border-solid tw-border-neutral-200 tw-pt-1;
    }
  }
</style>
